// Pattern: docs/design-patterns/frontend/component-patterns.md (layout-structure-only)
// Pattern: docs/design-patterns/frontend/accessibility.md
// Pattern: docs/design-patterns/frontend/ui-component-states.md
//
// Generic column shell for boards: status icon, label, count, add button,
// header actions slot, and a scrollable body. Props in, JSX out — imports NO
// drag-and-drop library. bodyRef/bodyProps/placeholder are pass-through slots
// a dnd consumer wires up itself (droppableProvided.innerRef -> bodyRef,
// droppableProvided.droppableProps -> bodyProps, droppableProvided.placeholder
// -> placeholder), mirroring the proven structure in
// src/pages/Projects/Board/Column.tsx (droppable ref on the scroll container,
// placeholder inside the scroll body) without touching that file.
//
// FE-003: onReachEnd/hasMore/isLoadingMore add infinite-scroll to the scroll
// body via useLoadMoreSentinel, using ScrollArea's forwarded ref (FE-004) as
// the IntersectionObserver root. Kept generic (no "project" vocabulary in any
// rendered text) so this shell stays reusable outside the product board.
import type { HTMLAttributes, JSX, ReactNode } from 'react';
import { useRef } from 'react';
import { Plus } from 'lucide-react';

import { useLoadMoreSentinel } from '~/hooks/useLoadMoreSentinel';

import { ScrollArea } from '~/components/ScrollArea';

export interface BoardColumnProps {
  /** Column title. */
  label: string;
  /** Item count badge; rendered only when defined (0 renders "0", not hidden). */
  count?: number;
  /** Status icon rendered before the label — caller composes it (e.g. from `status.ts`). */
  statusIcon?: ReactNode;
  /** Shows the add button when provided; invoked when the add button is activated. */
  onAdd?: () => void;
  /** Accessible name for the add button. Defaults to 'Add item' when omitted. */
  addLabel?: string;
  /** Opaque content rendered in the header, before the add button. */
  headerActions?: ReactNode;
  /** The column's cards, rendered inside the scrollable body. */
  children: ReactNode;
  /** Drag-and-drop pass-through: ref for the scroll body's wrapper element. */
  bodyRef?: (el: HTMLElement | null) => void;
  /** Drag-and-drop pass-through: props spread onto the scroll body's wrapper element. */
  bodyProps?: HTMLAttributes<HTMLDivElement>;
  /** Drag-and-drop pass-through: rendered after `children`, inside the scroll body. */
  placeholder?: ReactNode;
  /** Called when the scroll body's sentinel becomes visible while `hasMore` is
   * true and no fetch is already in flight — typically `fetchNextPage`. */
  onReachEnd?: () => void;
  /** Whether more items exist beyond `children`. Renders the sentinel (and
   * arms `useLoadMoreSentinel`) when true. Defaults to `false`. */
  hasMore?: boolean;
  /** Whether a next-page fetch is in flight. Shows the loading footer,
   * announces via the live region, and marks the scroll area `aria-busy`.
   * Also gates the sentinel so it can't re-fire mid-fetch. Defaults to `false`. */
  isLoadingMore?: boolean;
}

/** Stable no-op so `useLoadMoreSentinel` always gets a callback even when
 * `onReachEnd` is omitted (a caller not using pagination at all). */
function noop(): void {}

/**
 * Generic board column shell. See `BoardColumnProps` for the slot contract.
 */
export function BoardColumn({
  addLabel,
  bodyProps,
  bodyRef,
  children,
  count,
  hasMore = false,
  headerActions,
  isLoadingMore = false,
  label,
  onAdd,
  onReachEnd,
  placeholder,
  statusIcon,
}: BoardColumnProps): JSX.Element {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  // enabled is hasMore && !isLoadingMore (not just hasMore) so a sentinel
  // still visible while a fetch is already in flight can't re-fire onReachEnd
  // — matches useLoadMoreSentinel's own documented `enabled` contract.
  const sentinelRef = useLoadMoreSentinel(
    scrollAreaRef,
    onReachEnd ?? noop,
    hasMore && !isLoadingMore,
  );

  return (
    <div
      aria-label={label}
      className="flex-1 min-w-0 flex flex-col min-h-0 overflow-hidden rounded-lg border border-default"
      role="region"
    >
      <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-2 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {statusIcon}
          <h3 className="text-sm font-semibold text-foreground truncate">{label}</h3>
          {count !== undefined && (
            <span className="text-xs font-medium text-muted bg-surface-elevated rounded-full px-2 py-0.5">
              {count}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {headerActions}
          {onAdd && (
            <button
              type="button"
              onClick={onAdd}
              className="p-3 hover:bg-surface-elevated dark:hover:bg-surface-elevated rounded-full transition-colors duration-200"
              aria-label={addLabel ?? 'Add item'}
            >
              <Plus className="h-5 w-5 text-secondary-600 dark:text-muted" />
            </button>
          )}
        </div>
      </div>

      {/* bodyRef/bodyProps go on this wrapper (not on ScrollArea, which forwards
          neither) so a dnd consumer's droppable ref/props land on a stable
          container; children + placeholder render inside the ScrollArea, mirroring
          Column.tsx's droppable-ref-on-container / placeholder-inside-scroll-body. */}
      <div ref={bodyRef} {...bodyProps} className="flex-1 min-h-0 flex flex-col">
        <ScrollArea
          ref={scrollAreaRef}
          ariaBusy={isLoadingMore}
          className="flex-1 overflow-y-auto px-4 pb-4"
          padding={false}
        >
          <div className="space-y-2 py-2">{children}</div>
          {placeholder}
          {/* Invisible marker observed by useLoadMoreSentinel; aria-hidden since
              it carries no content of its own — the live region below is the
              actual accessible announcement. */}
          {hasMore && (
            <div
              ref={sentinelRef}
              aria-hidden="true"
              className="h-px"
              data-testid="board-column-sentinel"
            />
          )}
          {isLoadingMore && (
            <p aria-hidden="true" className="py-2 text-center text-xs text-muted">
              Loading more…
            </p>
          )}
        </ScrollArea>
      </div>

      {/* Always rendered (not just while loading) per frontend-accessibility's
          live-regions rule — a region only added to the DOM once loading
          starts is invisible to screen readers that already loaded the page.
          Visually hidden; the visible "Loading more…" footer above is
          aria-hidden so this is the single source of the announcement. */}
      <div aria-atomic="true" aria-live="polite" className="sr-only">
        {isLoadingMore ? 'Loading more items…' : ''}
      </div>
    </div>
  );
}
